import React from 'react';
import PropTypes from 'prop-types';
import './NewSeatsLayout.scss';
import { Placeholder } from '@reservamos/elements';
import LabelLoading from 'ui/molecules/LabelLoading';

/**
 * Renders the layout for new seats.
 *
 * @param {Object} props - The component props.
 * @param {ReactNode} props.diagramContent - The main diagram content (bus diagram or overbooking).
 * @param {ReactNode} props.toggle - The toggle diagram component.
 * @param {ReactNode} props.pricing - The pricing component.
 * @param {ReactNode} props.seatsLegend - The seats legend component.
 * @param {ReactNode} props.mobileHeader - The mobile header component.
 * @param {boolean} props.isFlat - The isFlat diagram component.
 * @param {boolean} props.isLoading - Indicates if the layout is in loading state.
 * @param {boolean} props.hasOverbooking - Whether the trip has overbooking enabled.
 * @param {string} props.variant - Optional layout variant.
 * @returns {ReactNode} The rendered component.
 */
const NewSeatsLayout = ({
  diagramContent,
  pricing,
  seatsLegend,
  toggle,
  mobileHeader,
  isLoading,
  isFlat,
  hasOverbooking,
  variant,
}) => {
  const loadingPlaceholder = <Placeholder height="280px" smallHeight="480px" />;
  const loadingText = <LabelLoading loadingText="searching_available_seats" />;
  const legendLoading = isFlat ? (
    <Placeholder height="40px" smallHeight="480px" />
  ) : (
    loadingPlaceholder
  );
  let loadingClass = '';
  let layoutClass = '';

  if (isLoading) {
    loadingClass = isFlat ? 'new-seats-layout-flat-loading' : 'new-seats-layout-loading';
  }

  if (isFlat) {
    layoutClass = hasOverbooking
      ? 'new-seats-layout-flat new-seats-layout-flat-overbooking'
      : 'new-seats-layout-flat';
  }

  const baseClass =
    variant === 'quickCheckout' ? 'new-seats-layout-quickCheckout' : 'new-seats-layout';

  return (
    <div id="seats-layout" className={`${baseClass} ${layoutClass} ${loadingClass}`}>
      <div className={`new-seats-layout-header ${isLoading ? '' : 'show-for-mobile-only'}`}>
        {isLoading ? loadingText : mobileHeader}
      </div>

      {diagramContent && (
        <div className="new-seats-layout-diagram">
          {isLoading ? loadingPlaceholder : diagramContent}
        </div>
      )}

      {isFlat && !isLoading && toggle && <div className="new-seats-layout-toggle">{toggle}</div>}

      <div className="new-seats-layout-legend">{isLoading ? legendLoading : seatsLegend}</div>

      <div className="new-seats-layout-pricing">{isLoading ? loadingPlaceholder : pricing}</div>

      {isFlat && <div className="new-seats-layout-empty-space" />}
    </div>
  );
};

NewSeatsLayout.propTypes = {
  diagramContent: PropTypes.node,
  pricing: PropTypes.node,
  seatsLegend: PropTypes.node,
  mobileHeader: PropTypes.node,
  toggle: PropTypes.node,
  isLoading: PropTypes.bool,
  isFlat: PropTypes.bool,
  hasOverbooking: PropTypes.bool,
  variant: PropTypes.oneOf(['quickCheckout']),
};

NewSeatsLayout.defaultProps = {
  diagramContent: null,
  isLoading: false,
  isFlat: false,
  hasOverbooking: false,
  variant: undefined,
};

export default NewSeatsLayout;
